import { React, useState, useEffect } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import {
  Multiselect,
  EnhancedAutocomplete,
  CreateValidateTextField,
  LocationAutocomplete,
} from "..";
import { Dialog, DialogTitle, FilledInput, Snackbar, Alert } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

function JobEdit(props) {
  const { userType, username, updateTable, setUpdateTable, job, setJobOffset } = props;

  // Same as JobSearch.js
  const [title, setTitle] = useState(job.title);
  const [location, setLocation] = useState(job.location);
  const [companyName, setCompanyName] = useState(job.company);
  const [skills, setSkills] = useState(job.skills);

  // Different from JobSearch.js
  const [jobDetails, setJobDetails] = useState(job.details);
  const [howToApply, setHowToApply] = useState(job.applyDetails);
  const [startDate, setStartDate] = useState(job.startDate);
  const [endDate, setEndDate] = useState(job.endDate);
  const [applyLink, setApplyLink] = useState(job.link);
  const [companyLink, setCompanyLink] = useState(job.companyLink);

  const [openEditDialog, setOpenEditDialog] = useState(false);

  const [jobLevel, setJobLevel] = useState(job.jobLevel);
  const [jobType, setJobType] = useState(job.jobType);
  const [jobIndustry, setJobIndustry] = useState(job.jobIndustry);

  const [availableSkills, setAvailableSkills] = useState([]);

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, "0"); // add 1 to month because getMonth() returns 0-based index, and pad with leading zero if necessary
  const currentDay = currentDate.getDate().toString().padStart(2, "0"); // pad with leading zero if necessary
  const formattedCurrentDate = `${currentYear}-${currentMonth}-${currentDay}`;

  const nextDate = new Date();
  nextDate.setDate(currentDate.getDate() + 1);
  const nextYear = nextDate.getFullYear();
  const nextMonth = (nextDate.getMonth() + 1).toString().padStart(2, "0"); // add 1 to month because getMonth() returns 0-based index, and pad with leading zero if necessary
  const nextDay = nextDate.getDate().toString().padStart(2, "0"); // pad with leading zero if necessary
  const formattedNextDate = `${nextYear}-${nextMonth}-${nextDay}`;

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  useEffect(() => {
    // Set the fields back to the job's data
    setTitle(job.title);
    setLocation(job.location);
    setCompanyName(job.company);
    setSkills(job.skills);
    setJobDetails(job.details);
    setHowToApply(job.applyDetails);
    setStartDate(job.startDate);
    setEndDate(job.endDate);
    setApplyLink(job.link);
    setCompanyLink(job.companyLink);
    setJobLevel(job.jobLevel);
    setJobType(job.jobType);
    setJobIndustry(job.jobIndustry);
  }, [job, openEditDialog]);

  // useEffect(() => {
  //   console.log("job edit, changing jobid", job.jobId);
  //   setJobOffset(job.jobId);
  // }, []);

  const handleEditJob = (event, draft) => {
    event.preventDefault();

    const body = new FormData();
    body.append("username", username);
    body.append("title", title);
    body.append("companyName", userType === "admin" ? companyName : username);
    body.append("userType", userType);
    body.append("location", location);
    body.append("jobDetails", jobDetails);
    body.append("startDate", startDate.toString());
    body.append("endDate", endDate.toString());
    body.append("applyLink", applyLink);
    body.append("companyLink", companyLink);
    body.append("applyDetails", howToApply);
    body.append("jobLevel", jobLevel);
    body.append("skills", skills);
    body.append("user_type", userType);
    body.append("jobType", jobType);
    body.append("jobIndustry", jobIndustry);
    body.append("draft", draft);
    body.append("jobId", job.jobId);

    axios.post("/company/editJob", body).then((res) => {
      const data = res.data;
      if (data.success) {
        resetHooks();
        setOpenEditDialog(false);
        setUpdateTable(!updateTable);
      } else {
        setSnackbarMessage(data.message);
        setSnackbarOpen(true);
      }
    });
  };


  useEffect(() => {
    axios.get("/company/skills").then((res) => {
      const data = res.data;
      if (data.success) {
        setAvailableSkills(data.skills);
      } else {
        return setAvailableSkills([]);
      }
    });
  }, []);

  const jobLevelData = {
    options: ["Internship", "Co-op", "Full-Time", "Part-Time"],
    value: jobLevel,
    setter: setJobLevel,
    placeholder: "Select job level (at least one)",
  };

  const jobTypeData = {
    options: ["In Person", "Remote", "Hybrid"],
    value: jobType,
    setter: setJobType,
    placeholder: "Select job type (at least one)",
  };

  const industryData = {
    options: [
      "Software Development",
      "Software Engineering",
      "Project Management",
      "Other",
    ],
    value: jobIndustry,
    setter: setJobIndustry,
    placeholder: "Select industry (at least one)",
  };

  function makeMultiselect(type) {
    let data = {};
    if (type === "Job Levels") {
      data = jobLevelData;
    } else if (type === "Job Types") {
      data = jobTypeData;
    } else if (type === "Job Industries") {
      data = industryData;
    } else {
      return;
    }

    return (
      <div className="[&>div>div]:mb-2">
        <Multiselect
          options={data.options}
          selectedValue={data.value}
          selectedSetter={data.setter}
        />
      </div>
    );
  }

  const resetHooks = () => {
    setTitle("");
    setLocation("");
    setCompanyName("");
    setJobLevel([]);
    setJobType([]);
    setJobIndustry([]);
    setSkills([]);
    setStartDate(formattedCurrentDate);
    setEndDate(formattedNextDate);
    setJobDetails("");
    setHowToApply("");
    setApplyLink("");
    setCompanyLink("");
  };

  function showEditDialog() {
    return (
      <Dialog open={openEditDialog} maxWidth={false}>
        <DialogTitle className="JobEdit-title mx-auto text-2xl font-bold font-brand-primary">
          Edit Job Posting
        </DialogTitle>
        <div className="JobEdit-modalContainer flex w-fit mb-4 mx-4">
          <div className="JobEdit-modalLeftContainer flex flex-col w-fit">
            <div className="JobEdit-titleContainer flex flex-col w-96 mx-4 mb-2">
              <div className="JobEdit-title text-lg font-brand-primary">
                Title *
              </div>
              <CreateValidateTextField
                placeholder="Junior Software Engineer"
                pattern="(?=.*\w).+"
                value={title}
                setter={setTitle}
                errorMessage={"Invalid Title."}
                required={true}
                maxLength={250}
              />
            </div>
            <div className="JobEdit-locationContainer flex flex-col w-96 mx-4 mb-2">
              <div className="JobEdit-locationTitle text-lg font-brand-primary">
                Location *
              </div>
              <LocationAutocomplete
                idAndClassName="JobEdit-LocationSearch"
                locationValue={location}
                setLocationValue={setLocation}
                key={200}
              />
            </div>
            <div className="JobEdit-jobDescriptionContainer flex flex-col w-96 mx-4 mb-2">
              <div className="JobEdit-jobDescriptionTitle text-lg font-brand-primary">
                Job Description *
              </div>
              <textarea
                value={jobDetails}
                onChange={(event) => {
                  if (event.target.value.length <= 10000) {
                    setJobDetails(event.target.value);
                  }
                }}
                className="rounded-xl bg-white p-2 border border-gray-300"
                type="textarea"
                placeholder="Please add details about the job here!"
                required={true}
              />
            </div>
            <div className="JobEdit-howToApplyContainer flex flex-col w-96 mx-4 mb-2">
              <div className="JobEdit-howToApplyTitle text-lg font-brand-primary">
                How To Apply *
              </div>
              <textarea
                value={howToApply}
                onChange={(event) => {
                  if (event.target.value.length <= 1000) {
                    setHowToApply(event.target.value);
                  }
                }}
                className="rounded-xl p-2  border border-gray-300"
                type="textarea"
                placeholder="Please add details about how to apply to the job here!"
                required={true}
              />
            </div>
            <div className="JobEdit-dateContainer flex justify-between">
              <div className="JobEdit-startDateContainer flex flex-col w-44 ml-4 mb-2">
                <div className="JobEdit-startDateTitle text-lg font-brand-primary">
                  Posting Start Date *
                </div>
                <FilledInput
                  required
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disableUnderline={true}
                  className="rounded-xl bg-white border border-gray-300 h-11 [&>input]:pt-2"
                />
              </div>
              <div className="JobEdit-endDateContainer flex flex-col w-44 mr-4 mb-2">
                <div className="JobEdit-endDateTitle text-lg font-brand-primary">
                  Posting End Date *
                </div>
                <FilledInput
                  required
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disableUnderline={true}
                  className="rounded-xl h-11 bg-white border border-gray-300 [&>input]:pt-2"
                />
              </div>
            </div>
            {userType == "admin" && (
              <div className="JobEdit-companyContainer flex flex-col w-96 mx-4 mb-2">
                <div className="JobEdit-companyTitle text-lg font-brand-primary">
                  Company Name *
                </div>
                <CreateValidateTextField
                  placeholder="Company"
                  pattern="(?=.*\w).+"
                  value={companyName}
                  setter={setCompanyName}
                  errorMessage={"Invalid Company."}
                  required={true}
                />
              </div>
            )}
          </div>
          <div className="JobEdit-modalRightContainer flex flex-col w-fit">
            <div className="JobEdit-applicationLinkContainer flex flex-col w-96 mx-4 mb-2">
              <div className="JobEdit-applicationLinkTitle text-lg font-brand-primary">
                Application Link *
              </div>
              <CreateValidateTextField
                placeholder="https://yoursite.com or youremail@email.com"
                pattern="(https:\/\/)?(www\.)?((?=[a-zA-Z])[a-zA-Z0-9_-]+(?<=[a-zA-Z])\.)+[a-zA-Z]{2,}\/?(\/?[a-zA-Z0-9-#?_]*)*|[a-zA-Z_.%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
                value={applyLink}
                setter={setApplyLink}
                errorMessage={"Invalid Link. Must be Link or Email."}
                required={true}
                maxLength={1000}
              />
            </div>
            <div className="JobEdit-companyLinkContainer flex flex-col w-96 mx-4 mb-2">
              <div className="JobEdit-companyLinkTitle text-lg font-brand-primary">
                Company Link *
              </div>
              <CreateValidateTextField
                placeholder="https://yoursite.com"
                pattern="(https:\/\/)?(www\.)?((?=[a-zA-Z])[a-zA-Z0-9_-]+(?<=[a-zA-Z])\.)+[a-zA-Z]{2,}\/?(\/?[a-zA-Z0-9-#?_]*)*"
                value={companyLink}
                setter={setCompanyLink}
                errorMessage={"Invalid Link. Must be Link."}
                required={true}
              />
            </div>
            <div className="JobEdit-jobLevelContainer flex flex-col w-96 mx-4">
              <div className="JobEdit-JobLevelTitle text-lg font-brand-primary">
                Job Level (Please choose at least one): *
              </div>
              {makeMultiselect("Job Levels")}
            </div>
            <div className="JobEdit-jobTypeContainer flex flex-col w-96 mx-4">
              <div className="JobEdit-jobTypeTitle text-lg font-brand-primary">
                Job Type (Please choose at least one): *
              </div>
              {makeMultiselect("Job Types")}
            </div>
            <div className="JobEdit-industriesContainer flex flex-col w-96 mx-4">
              <div className="JobEdit-industriesTitle text-lg font-brand-primary">
                Industries (Please choose at least one): *
              </div>
              {makeMultiselect("Job Industries")}
            </div>
            <div className="JobEdit-jobSkillsContainer flex flex-col w-96 mx-4 mb-4">
              <div className="JobEdit-jobSkillsTitle text-lg font-brand-primary">
                Job Skills:
                <div className="[&>div>div]:mt-1">
                  <EnhancedAutocomplete
                    placeholder="Select skill(s)"
                    options={availableSkills}
                    selectedValue={skills}
                    selectedSetter={setSkills}
                    isMultiSelect={true}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="JobEdit-buttonContainer flex justify-between px-8 mb-4">
          <button
            type="button"
            className="primary-button bg-brand-secondary mb-2 h-11 pt-3"
            onClick={(event) => handleEditJob(event, true)}
          >
            Update As Draft
          </button>
          <button
            type="button"
            className="primary-button bg-brand-quaternary mb-2 h-11 pt-3"
            onClick={(event) => handleEditJob(event, false)}
          >
            Update Job
          </button>
          <button
            type="button"
            className="primary-button bg-brand-primary  mb-2 h-11 pt-3"
            onClick={resetHooks}
          >
            Reset
          </button>
          <button
            type="button"
            className="primary-button bg-brand-primary mb-2 h-11 pt-3"
            onClick={() => {
              setOpenEditDialog(false);
              resetHooks();
            }}
          >
            Cancel
          </button>
        </div>
        <Snackbar
          open={snackbarOpen}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          autoHideDuration={6000}
          onClose={() => {
            setSnackbarOpen(false);
            setSnackbarMessage("");
          }
          }
        >
          <Alert
            onClose={() => {
              setSnackbarOpen(false);
              setSnackbarMessage("");
            }
            }
            severity="error"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Dialog>
    );
  }

  return (
    <div className="JobEdit-container">
      {showEditDialog()}
      <button type="button" onClick={() => setOpenEditDialog(true)}>
        <EditIcon fontSize="large" className="fill-brand-quaternary" />
      </button>
    </div>
  );
}

JobEdit.propTypes = {
  userType: PropTypes.string.isRequired,
  username: PropTypes.string.isRequired,
  updateTable: PropTypes.bool.isRequired,
  setUpdateTable: PropTypes.func.isRequired,
  job: PropTypes.shape({
    jobId: PropTypes.number,
    title: PropTypes.string,
    location: PropTypes.string,
    company: PropTypes.string,
    details: PropTypes.string,
    link: PropTypes.string,
    companyLink: PropTypes.string,
    applyDetails: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    jobType: PropTypes.arrayOf(PropTypes.string),
    jobLevel: PropTypes.arrayOf(PropTypes.string),
    jobIndustry: PropTypes.arrayOf(PropTypes.string),
    skills: PropTypes.arrayOf(PropTypes.string),
    viewCount: PropTypes.number,
    applyCount: PropTypes.number,
    favorited: PropTypes.string
  }),
  setJobOffset: PropTypes.func
};

export default JobEdit;
